import type { StoreItem } from '../types/get-store-items';
import { calculateMinPrice } from './calculate-min-price';
import { getPromotion } from './promotion';

/**
 * Preço pronto para exibição no cardápio, já aplicando a regra "De/Por".
 *
 * - `price` ("Por"): valor em destaque, sempre o preço efetivamente cobrado —
 *   preço de venda promocional quando há promoção válida, senão o preço cheio —
 *   somado ao piso de adicionais obrigatórios.
 * - `regularPrice` ("De"): preço cheio (também somado ao piso obrigatório),
 *   exibido riscado. É `null` quando não há promoção válida, sinalizando que a
 *   apresentação De/Por não deve aparecer.
 * - `hasRequiredAddons`: há categorias obrigatórias, ou seja, o preço é um
 *   "a partir de".
 */
export type DisplayPrice = {
  price: number;
  regularPrice: number | null;
  hasRequiredAddons: boolean;
};

/**
 * Resolve o preço de exibição de um item de cardápio aplicando a regra De/Por.
 *
 * Centraliza o cálculo antes duplicado nos cards de produto: o preço efetivo
 * (com desconto, quando válido) mais o piso de adicionais obrigatórios vira o
 * "Por"; o preço cheio mais o mesmo piso vira o "De". O piso de adicionais é
 * independente da promoção (adicionais não entram na oferta), então soma-se o
 * mesmo `addonFloor` em ambos os lados.
 *
 * Só há "De" quando {@link getPromotion} considera a promoção válida (preço de
 * venda finito, positivo e estritamente menor que o cheio), evitando exibir
 * descontos inexistentes ou negativos. Item nulo/indefinido retorna um preço
 * zerado e sem promoção (default seguro).
 */
export function getDisplayPrice(item: StoreItem | null | undefined): DisplayPrice {
  if (!item) {
    return { price: 0, regularPrice: null, hasRequiredAddons: false };
  }

  const { minPrice, addonFloor, hasRequiredAddons } = calculateMinPrice(item);
  const promotion = getPromotion(item);

  const regularPrice = promotion ? promotion.regularPrice + addonFloor : null;

  return { price: minPrice, regularPrice, hasRequiredAddons };
}
